import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { vitalSignsService } from '../services/vitalSignsService'
import { formatDate } from '../utils/locale'
import { formatTemperature } from '../utils/vitalSignsUtils'
import { UnitPreference } from '../types/vitalSigns'
import { MaskedField } from './MaskedField'
import './VitalSignsChart.css'

interface VitalSignsChartProps {
  patientId: string
  vitalType?: 'bloodPressure' | 'heartRate' | 'temperature' | 'respiratoryRate' | 'weight' | 'height' | 'bmi'
  userPreference?: UnitPreference
  dateRange?: { startDate?: string; endDate?: string }
  height?: number
}

export function VitalSignsChart({
  patientId,
  vitalType = 'bloodPressure',
  userPreference = 'metric',
  dateRange,
  height = 350,
}: VitalSignsChartProps) {
  const { data: vitalSigns, isLoading } = useQuery({
    queryKey: ['vital-signs', 'patient', patientId, dateRange],
    queryFn: () => vitalSignsService.getByPatient(patientId),
    enabled: !!patientId,
  })

  const chartData = useMemo(() => {
    if (!vitalSigns || vitalSigns.length === 0) return []

    // Filter and map vital signs to data points
    const dataPoints = vitalSigns
      .filter((vs) => {
        if (dateRange?.startDate && vs.recordedAt < dateRange.startDate) return false
        if (dateRange?.endDate && vs.recordedAt > dateRange.endDate) return false
        return true
      })
      .map((vs) => {
        const date = new Date(vs.recordedAt || vs.createdAt)
        const dataPoint: any = {
          date: date.toISOString().split('T')[0],
          dateTime: date.getTime(),
          recordedAt: vs.recordedAt || vs.createdAt,
        }

        // Blood Pressure
        if (vs.bloodPressure) {
          dataPoint.systolic = vs.bloodPressure.systolic
          dataPoint.diastolic = vs.bloodPressure.diastolic
        }

        // Heart Rate
        if (vs.heartRate) {
          dataPoint.heartRate = vs.heartRate.value
        }

        // Temperature
        if (vs.temperature) {
          dataPoint.temperature = vs.temperature.value
        }

        // Respiratory Rate
        if (vs.respiratoryRate) {
          dataPoint.respiratoryRate = vs.respiratoryRate.value
        }

        // Weight
        if (vs.weight) {
          dataPoint.weight = vs.weight.value
        }

        // Height
        if (vs.height) {
          dataPoint.height = vs.height.value
        }

        // BMI
        if (vs.bmi) {
          dataPoint.bmi = vs.bmi
        }

        return dataPoint
      })
      .sort((a, b) => a.dateTime - b.dateTime)

    if (dataPoints.length === 0) return []

    // Fill in missing dates to ensure lines connect across gaps
    const filledData: any[] = []
    const dataMap = new Map<string, any>()
    dataPoints.forEach((point) => {
      dataMap.set(point.date, point)
    })

    const startDate = new Date(dataPoints[0].date)
    const endDate = new Date(dataPoints[dataPoints.length - 1].date)
    const currentDate = new Date(startDate)

    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0]
      const existingPoint = dataMap.get(dateStr)
      
      if (existingPoint) {
        filledData.push(existingPoint)
      } else {
        // Create a null data point for missing dates
        filledData.push({
          date: dateStr,
          dateTime: currentDate.getTime(),
          // All vital sign values will be null/undefined, which connectNulls will handle
        })
      }
      
      currentDate.setDate(currentDate.getDate() + 1)
    }

    return filledData
  }, [vitalSigns, dateRange])

  if (isLoading) {
    return <div className="vital-signs-chart loading">Loading chart data...</div>
  }

  if (!vitalSigns || vitalSigns.length === 0) {
    return <div className="vital-signs-chart empty">No vital signs data available</div>
  }

  return (
    <MaskedField
      field="vital-signs"
      value={vitalSigns}
      patientId={patientId}
      confidentialityLevel="restricted"
    >
      {(canAccess, _maskedVitalSigns, requestEmergencyAccess) => {
        if (!canAccess) {
          return (
            <div className="vital-signs-chart restricted">
              <p>You do not have permission to view vital signs.</p>
              {requestEmergencyAccess && (
                <button
                  onClick={requestEmergencyAccess}
                  className="btn-secondary btn-small"
                  style={{ marginTop: '1rem' }}
                >
                  Request Emergency Access
                </button>
              )}
            </div>
          )
        }

        if (chartData.length === 0) {
          return <div className="vital-signs-chart empty">No data in selected date range</div>
        }

        const renderChart = () => {
    switch (vitalType) {
      case 'bloodPressure':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => formatDate(value)}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis label={{ value: 'mmHg', angle: -90, position: 'insideLeft' }} />
              <Tooltip
                labelFormatter={(value) => formatDate(value)}
                formatter={(value: any, name?: string) => {
                  if (name === 'systolic' || name === 'diastolic') {
                    return [`${value} mmHg`, name || '']
                  }
                  return [value, name || '']
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="systolic"
                stroke="#e74c3c"
                strokeWidth={2}
                dot={{ r: 4 }}
                name="Systolic"
                connectNulls={true}
              />
              <Line
                type="monotone"
                dataKey="diastolic"
                stroke="#3498db"
                strokeWidth={2}
                dot={{ r: 4 }}
                name="Diastolic"
                connectNulls={true}
              />
            </LineChart>
          </ResponsiveContainer>
        )

      case 'heartRate':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => formatDate(value)}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis label={{ value: 'bpm', angle: -90, position: 'insideLeft' }} />
              <Tooltip
                labelFormatter={(value) => formatDate(value)}
                formatter={(value: any) => [`${value} bpm`, 'Heart Rate']}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="heartRate"
                stroke="#27ae60"
                strokeWidth={2}
                dot={{ r: 4 }}
                name="Heart Rate"
                connectNulls={true}
              />
            </LineChart>
          </ResponsiveContainer>
        )

      case 'temperature':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => formatDate(value)}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis
                label={{ value: userPreference === 'imperial' ? '°F' : '°C', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip
                labelFormatter={(value) => formatDate(value)}
                formatter={(value: any) => [
                  formatTemperature(value, userPreference === 'imperial' ? 'fahrenheit' : 'celsius'),
                  'Temperature',
                ]}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="temperature"
                stroke="#e67e22"
                strokeWidth={2}
                dot={{ r: 4 }}
                name="Temperature"
                connectNulls={true}
              />
            </LineChart>
          </ResponsiveContainer>
        )

      case 'respiratoryRate':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => formatDate(value)}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis label={{ value: 'breaths/min', angle: -90, position: 'insideLeft' }} />
              <Tooltip
                labelFormatter={(value) => formatDate(value)}
                formatter={(value: any) => [`${value} /min`, 'Respiratory Rate']}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="respiratoryRate"
                stroke="#9b59b6"
                strokeWidth={2}
                dot={{ r: 4 }}
                name="Respiratory Rate"
              />
            </LineChart>
          </ResponsiveContainer>
        )

      case 'weight':
      case 'height':
      case 'bmi':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => formatDate(value)}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis
                label={{
                  value:
                    vitalType === 'weight'
                      ? userPreference === 'imperial'
                        ? 'lb'
                        : 'kg'
                      : vitalType === 'height'
                        ? userPreference === 'imperial'
                          ? 'in'
                          : 'cm'
                        : 'BMI',
                  angle: -90,
                  position: 'insideLeft',
                }}
              />
              <Tooltip
                labelFormatter={(value) => formatDate(value)}
                formatter={(value: any) => [value, vitalType.toUpperCase()]}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey={vitalType}
                stroke="#16a085"
                strokeWidth={2}
                dot={{ r: 4 }}
                name={vitalType.toUpperCase()}
                connectNulls={true}
              />
            </LineChart>
          </ResponsiveContainer>
        )

      default:
        return null
    }
    }

    return (
      <div className="vital-signs-chart">
        <div className="vital-signs-chart-header">
          <h3>{vitalType.charAt(0).toUpperCase() + vitalType.slice(1).replace(/([A-Z])/g, ' $1')}</h3>
        </div>
        <div className="vital-signs-chart-container">{renderChart()}</div>
      </div>
    )
      }}
    </MaskedField>
  )
}
